import React, {useMemo} from 'react';

import KpiItem from './KpiItem';
import {KpiWidgetItem} from '../../types';
import {WidgetProps, Trans} from '@deviceinsight/ng-ui-scale-lib/api';
import useAssetsFromConfig from '../../hooks/useAssetsFromConfig';
import {VerticalSortContext} from '../../../../../VerticalSortContext';

type Props = {
	items: KpiWidgetItem[];
	context: WidgetProps['context'];
	onItemChange: (index: number, item: KpiWidgetItem) => void;
	onItemRemove: (index: number) => void;
	onOrderChange: (items: KpiWidgetItem[]) => void;
	setSelectingAsset: (index: number) => void;
	setSelectingKpi: (index: number, assetUrn: string) => void;
	withUnit: boolean;
	withChartType: boolean;
};

type SortableItem = KpiWidgetItem & {id: string};
export default function SortableKpiItemConfigTable({
	items,
	context,
	onItemChange,
	onItemRemove,
	onOrderChange,
	setSelectingKpi,
	setSelectingAsset,
	withChartType,
	withUnit
}: Props) {
	const assets = useAssetsFromConfig(items, context);
	const sortableItems = useMemo<SortableItem[]>(() => items.map((item) => ({...item, id: item.uuid})), [items]);

	if (items.length === 0) return null;
	return (
		<VerticalSortContext
			items={sortableItems}
			//eslint-disable-next-line @typescript-eslint/no-unused-vars
			onOrderChange={(items) => onOrderChange(items.map(({id, ...item}): KpiWidgetItem => item))}>
			<table className="di reactable">
				<thead>
					<tr>
						<th>
							<Trans i18nKey="dashboard.widget.kpiaggredatedchart.order">Order</Trans>
						</th>
						<th>
							<Trans i18nKey="dashboard.widget.kpiaggredatedchart.label">Label (optional)</Trans>
						</th>
						{context.type === 'assetGroup' && (
							<th>
								<Trans i18nKey="dashboard.widget.kpiaggredatedchart.asset">Asset</Trans>
							</th>
						)}
						<th>
							<Trans i18nKey="dashboard.widget.kpiaggredatedchart.kpiName">Kpi Name</Trans>
						</th>
						{withUnit && (
							<th>
								<Trans i18nKey="dashboard.widget.kpiaggredatedchart.unit">Unit</Trans>
							</th>
						)}
						<th>
							<Trans i18nKey="dashboard.widget.kpiaggredatedchart.color">Color</Trans>
						</th>
						{withChartType && (
							<th>
								<Trans i18nKey="dashboard.widget.kpiaggredatedchart.chartType">Display as</Trans>
							</th>
						)}
						<th>&nbsp;</th>
					</tr>
				</thead>
				<tbody>
					{items.map((item, i) => (
						<KpiItem
							key={`item-${i}`}
							context={context}
							item={item}
							asset={
								context.type === 'asset' ? context.asset : assets.find(({urn}) => urn === item.assetUrn)
							}
							itemIndex={i}
							withUnit={withUnit}
							withChartType={withChartType}
							setKpiSelection={setSelectingKpi}
							setAssetSelection={setSelectingAsset}
							onItemChange={onItemChange}
							onItemRemove={onItemRemove}
						/>
					))}
				</tbody>
			</table>
		</VerticalSortContext>
	);
}
